import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_CERTIFICATE } from '../../../lib/constants';
import { TCertificate } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_CERTIFICATE_TYPE_ENUM } from '../../../graph-queries/tar/certificate';
import {
  GET_EKG_FIELDS,
  GET_ECOW_PERMIT_BY_PERMIT_NUMBER,
  GET_MO_DETAILS,
} from '../../../graph-queries/tar/job';
import { DatabaseEnum } from '../../../graph-queries/__generated__/graphql';
import {
  convertToLabelMap,
  renderOptions,
  renderOptionsFromOptions,
} from '../../../lib/util/render';
import {
  useCertificationSelect,
  CertificationOption,
  customSelectStyles,
} from './CertificateForm.util';
import { GET_CONFIG } from '../../../graph-queries/common/config';
import { getCertificateAreaOptions } from '../../../lib/util/certificateOptions';
import { GET_ALL_SYSTEMS } from '../../../graph-queries/tar/system';

const CertificateForm: FC<{
  defaultValues?: TCertificate;
  onSubmit: (purchaesOrder: TCertificate) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const assetId = asset?.assetId || -1;
  const defaultMoValue = useRef(defaultValues?.maintenanceOrder);
  const defaultSystemValue = useRef(defaultValues?.system);
  const { data } = useQuery(GET_CERTIFICATE_TYPE_ENUM, {});
  const certificateTypes = (data?.certificateTypeEnum as unknown as DatabaseEnum[]) || [];

  const [fetchSapMo] = useLazyQuery(GET_MO_DETAILS);
  const [fetchEkgFields] = useLazyQuery(GET_EKG_FIELDS);

  const [moStatus, setMoStatus] = useState('');
  const [moValue, setMoValue] = useState(defaultValues?.maintenanceOrder || '');
  const [permitNumber, setPermitNumber] = useState(defaultValues?.permitNumber || '');
  const [floc, setFloc] = useState('');
  const [system, setSystem] = useState(defaultSystemValue.current || '');
  const [ekgError, setEkgError] = useState(false);
  const [useDropdown, setUseDropdown] = useState(false);
  const [titleValue, setTitleValue] = useState(defaultValues?.title || '');
  const [titleError, settitleError] = useState('');
  const [titleValidated, setTitleValidated] = useState(false);
  const [moHasChanged, setMoHasChanged] = useState(false);

  // edit mode, MO present, system missing
  const shouldFetchSystemOnMount = useRef(
    type === 'edit' && !defaultValues?.system && defaultValues?.maintenanceOrder
  );

  const defaultMc1Option: CertificationOption | undefined = defaultValues?.maintenanceMC1
    ? {
        label: defaultValues.maintenanceMC1.mc1Number,
        value: defaultValues.maintenanceMC1.mc1Number,
        id: defaultValues.maintenanceMC1.mc1Id,
      }
    : undefined;
  const {
    options: mc1Options,
    value: mc1Value,
    handleChange: handleChangeMc1,
    handleCreate: handleCreateMc1,
    creatingOption: creatingMc1,
  } = useCertificationSelect('mc1', assetId, defaultMc1Option);

  const defaultSh1Option: CertificationOption | undefined = defaultValues?.maintenanceSH1
    ? {
        label: defaultValues.maintenanceSH1.sh1Number,
        value: defaultValues.maintenanceSH1.sh1Number,
        id: defaultValues.maintenanceSH1.sh1Id,
      }
    : undefined;
  const {
    options: sh1Options,
    value: sh1Value,
    handleChange: handleChangeSh1,
    handleCreate: handleCreateSh1,
    creatingOption: creatingSh1,
  } = useCertificationSelect('sh1', assetId, defaultSh1Option);

  const CERTIFICATE_AREA_OPTIONS = getCertificateAreaOptions(assetId);

  const { data: config } = useQuery(GET_CONFIG);
  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;

  const {
    data: ecowPermitData,
    loading: ecowPermitLoading,
    error: ecowPermitError,
  } = useQuery(GET_ECOW_PERMIT_BY_PERMIT_NUMBER, {
    variables: { permitNumber: permitNumber },
    skip: !permitNumber,
  });

  // fetch system data for dropdown
  const {
    data: systemData,
    loading: systemsLoading,
    refetch: refetchSystems,
  } = useQuery(GET_ALL_SYSTEMS, {
    variables: { assetId },
    skip: !useDropdown, // Only fetch when dropdown needed
    fetchPolicy: 'cache-and-network', // Use cache but also check server
  });

  const systemOptions = useMemo(() => {
    return systemData?.allSystemsForAsset || [];
  }, [systemData]);

  useEffect(() => {
    const fetchFlocForMissingSystem = async () => {
      if (!shouldFetchSystemOnMount.current) {
        return;
      }

      console.log('Edit mode: System missing, fetching FLOC from MO');
      shouldFetchSystemOnMount.current = false; // Only run once

      const result = await fetchFlocFromMo(defaultValues!.maintenanceOrder!);

      if (!result.error && result.floc) {
        console.log('FLOC fetched:', result.floc);
        setFloc(result.floc);
      } else {
        // if error fetching FLOC, fetch system from DB
        await fetchSystemFromDB();
      }
    };
    fetchFlocForMissingSystem();
  }, []);

  useEffect(() => {
    // Extracting repeated error handling logic
    const handleEkgFetchError = async () => {
      setEkgError(true);
      await fetchSystemFromDB();
    };

    const fetchSystemFromEkg = async () => {
      if (!floc || floc.trim() === '') {
        return;
      }

      if (type === 'edit' && defaultSystemValue.current && !moHasChanged) {
        console.log('Skipping system fetch - using existing system value');
        return;
      }

      try {
        setEkgError(false);
        setUseDropdown(false);

        const { data: ekgData, error } = await fetchEkgFields({
          variables: { location: floc },
        });

        if (error || !ekgData?.ekgFields) {
          console.warn('Error fetching EKG fields:', error);
          await handleEkgFetchError();
          return;
        }

        if (ekgData.ekgFields) {
          const systemNum = ekgData.ekgFields.systemNumber || '';
          const systemDesc = ekgData.ekgFields.systemDescription || '';

          const combinedSystem = `${systemNum} - ${systemDesc}`;

          setSystem(combinedSystem);
          setUseDropdown(false);
        } else {
          await handleEkgFetchError();
        }
      } catch (error) {
        console.error('Error fetching EKG fields:', error);
        await handleEkgFetchError();
      }
    };
    fetchSystemFromEkg();
  }, [floc, fetchEkgFields, type, defaultValues?.system, moHasChanged]);

  const fetchFlocFromMo = async (
    moNumber: string
  ): Promise<{
    floc: string;
    status: string;
    error: boolean;
  }> => {
    try {
      const { data: sapData, error } = await fetchSapMo({
        variables: { moNumber, isRealTime: isRealTimeEnabled },
      });

      if (error || !sapData?.sapMo) {
        return { floc: '', status: 'error', error: true };
      }

      return {
        floc: sapData.sapMo.location || '',
        status: sapData.sapMo.statusFioriDescription || '',
        error: false,
      };
    } catch (error) {
      console.error('Error fetching SAP MO:', error);
      return { floc: '', status: 'error', error: true };
    }
  };

  const fetchSystemFromDB = async () => {
    try {
      setUseDropdown(true);
      const { data } = await refetchSystems();

      if (!data?.allSystemsForAsset || data.allSystemsForAsset.length === 0) {
        console.error('No systems found in DB for asset:', assetId);
        setUseDropdown(false);
      } else {
        setSystem('');
      }
    } catch (error) {
      console.error('Error fetching systems from DB:', error);
      setUseDropdown(false);
    }
  };

  const validateMoNumber = async (moNumber: string) => {
    if (!moNumber || moNumber.trim() === '') {
      setMoStatus('');
      setFloc('');
      setSystem('');
      setUseDropdown(false);
      setEkgError(false);
      return;
    }

    // Skip validation in edit mode if MO hasn't changed
    if (type === 'edit' && moNumber === defaultMoValue.current && !moHasChanged) {
      console.log('Skipping MO validation - using original value');
      return;
    }

    setMoStatus('');
    const result = await fetchFlocFromMo(moNumber);
    setFloc(result.floc);
    setMoStatus(result.error ? 'error' : result.status);
  };

  const validateCertificateTitle = (title: string) => {
    setTitleValidated(true);

    if (!title || title.trim() === '') {
      settitleError('');
      return;
    }

    const invalidCharacters = ['*', '?', ':', '\\', '/', '[', ']'];
    const foundInvalidChars = invalidCharacters.filter((char) => title.includes(char));

    if (foundInvalidChars.length > 0) {
      settitleError(
        'Invalid title. Title cannot include any of the following characters: *, ?, :, \\, /, [, ]'
      );
    } else {
      settitleError('');
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTitleValue(value);

    // Reset validation state on change
    setTitleValidated(false);

    //clear any existing errors
    if (titleError != '') {
      settitleError('');
    }
  };

  const handleTitleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validateCertificateTitle(e.target.value);
  };

  // Memoizing title validation state calculation
  const titleValidationState = useMemo(() => {
    if (!titleValidated) {
      return { valid: false, invalid: false }; // Neutral state - no validation performed
    }

    const hasValue = titleValue !== '' && titleValue.trim() !== '';
    const hasError = titleError !== '';

    return {
      valid: hasValue && !hasError,
      invalid: hasError,
    };
  }, [titleValidated, titleValue, titleError]);

  const handleMoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMoValue(value);

    // if in edit mode and value has changed from default, set moHasChanged to true
    if (type == 'edit' && value !== defaultMoValue.current) {
      setMoHasChanged(true);
    } else if (type == 'edit' && value === defaultMoValue.current) {
      setMoHasChanged(false);
    }

    if (moStatus !== '') {
      setMoStatus('');
    }
  };

  const handleMoBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'add' || moHasChanged) {
      validateMoNumber(e.target.value);
    }
  };

  // Helper function to check if form can be submitted
  const isFormSubmittable = () => {
    // check MO validation
    let isMoValid = true;
    if (moValue && moValue.trim() !== '') {
      isMoValid = moStatus !== 'error';
    }

    // check title validation
    let isTitleValid = true;
    if (titleValidated) {
      isTitleValid = titleValidationState.valid && !titleValidationState.invalid;
    }

    return isMoValid && isTitleValid;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string>;

    const certificate: TCertificate = {
      ...EMPTY_CERTIFICATE,
      ...inputData,
      assetId,
      certificateId: parseInt(inputData.certificateId),
      mc1Id: mc1Value?.id || -1,
      sh1Id: sh1Value?.id || -1,
      template: inputData.template === 'true' ? true : false,
      location: ecowPermitData?.ecowPermit.locationDescription || '',
    };

    if (defaultValues?.certificateId) certificate.certificateId = defaultValues.certificateId;

    if (defaultValues?.createdBy) certificate.createdBy = defaultValues.createdBy;

    onSubmit(certificate)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  const handleSystemChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setSystem(value);
  };

  const systemSelectOptions = useMemo(() => {
    return systemOptions.map((sys) => ({
      value: `${sys.systemNumber} - ${sys.description}`,
      label: `${sys.systemNumber} - ${sys.description}`, //Required for React Select
      systemId: sys.systemId,
    }));
  }, [systemOptions]);

  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      {type === 'edit' && (
        <div className="col-md-6">
          <FormGroup>
            <Label for="certificate-number">Certificate Number</Label>
            <Input
              type="text"
              name="certificateNumber"
              id="certificate-number"
              defaultValue={defaultValues?.certificateNumber || defaultValues?.certificateId || ''}
            />
          </FormGroup>
        </div>
      )}
      <div className={type === 'edit' ? 'col-md-6' : 'col-md-12'}>
        <FormGroup>
          <Label for="certificate-title">Title</Label>
          <Input
            valid={titleValidationState.valid}
            invalid={titleValidationState.invalid}
            type="text"
            name="title"
            id="certificate-title"
            required
            value={titleValue}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
          />
          {titleValidationState.invalid && (
            <div className="text-danger small mt-1">{titleError}</div>
          )}
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="maintenance-order">Maintenance Order</Label>
          <Input
            valid={moValue !== '' && moStatus !== '' && moStatus !== 'error'}
            invalid={moValue !== '' && moStatus === 'error'}
            type="text"
            name="maintenanceOrder"
            id="maintenance-order"
            placeholder="Enter MO i.e. 101530614"
            value={moValue}
            onChange={handleMoChange}
            onBlur={handleMoBlur}
          />
          {moStatus === 'error' && (
            <div className="text-danger small mt-1">Invalid maintenance order</div>
          )}
        </FormGroup>
      </div>
      <div className="col-md-3">
        <FormGroup>
          <Label for="certificate-type">Type</Label>
          <Input
            type="select"
            name="type"
            id="certificate-type"
            role="combobox"
            defaultValue={defaultValues?.type || ''}
            required
          >
            {renderOptions(convertToLabelMap(certificateTypes))}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-3">
        <FormGroup>
          <Label className="x5-me-6">Template</Label>
          <div className="d-flex">
            <FormGroup check className="me-3" inline>
              <Input
                id="template-1"
                name="template"
                type="radio"
                value="true"
                defaultChecked={defaultValues?.template === true}
              />
              <Label check for="template-1" className="ms-1">
                Yes
              </Label>
            </FormGroup>
            <FormGroup check className="me-0" inline>
              <Input
                id="template-2"
                name="template"
                type="radio"
                value="false"
                defaultChecked={defaultValues?.template === false}
              />
              <Label check for="template-2" className="ms-1">
                No
              </Label>
            </FormGroup>
          </div>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup title="Select existing from dropdown or type new and select Create.">
          <Label htmlFor="mc1-select">MC1</Label>
          <CreatableSelect
            id="mc1-select"
            placeholder="Select or type new"
            isClearable
            isLoading={creatingMc1}
            onChange={handleChangeMc1}
            onCreateOption={handleCreateMc1}
            options={mc1Options}
            value={mc1Value}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup title="Select existing from dropdown or type new and select Create.">
          <Label htmlFor="sh1-select">SH1</Label>
          <CreatableSelect
            id="sh1-select"
            placeholder="Select or type new"
            isClearable
            isLoading={creatingSh1}
            onChange={handleChangeSh1}
            onCreateOption={handleCreateSh1}
            options={sh1Options}
            value={sh1Value}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="permit-number">Permit Number</Label>
          <Input
            type="text"
            name="permitNumber"
            id="permit-number"
            value={permitNumber}
            onChange={(e) => setPermitNumber(e.target.value)}
            placeholder="i.e. 'BP-NS-CRDG#544692'"
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="isolation-number">Isolation Number</Label>
          <Input
            type="text"
            name="isolationNumber"
            id="isolation-number"
            defaultValue={defaultValues?.isolationNumber || ''}
            placeholder="i.e. 'BP-NS-CRDG#544692'"
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="lubricant-used">Lubricant Used</Label>
          <Input
            type="text"
            name="lubricantUsed"
            id="lubricant-used"
            defaultValue={defaultValues?.lubricantUsed || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="area">Area</Label>
          <Input
            type="select"
            name="area"
            id="area"
            role="combobox"
            required
            defaultValue={defaultValues?.area || ''}
          >
            <option value={''} disabled hidden>
              Select an area
            </option>
            {renderOptionsFromOptions(CERTIFICATE_AREA_OPTIONS)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="system">System</Label>
          {useDropdown ? (
            <Select
              id="system"
              name="system"
              placeholder="Select a system"
              options={systemSelectOptions}
              value={system ? systemSelectOptions.find((opt) => opt.value === system) : null}
              onChange={(selected) => {
                setSystem(selected ? selected.value : '');
              }}
              styles={customSelectStyles}
              menuPosition="fixed" // Keeps menu in viewport
              menuPlacement="auto"
              isClearable
              isSearchable
            />
          ) : (
            <>
              <Input
                type="text"
                name="system"
                id="system"
                value={system}
                onChange={handleSystemChange}
                placeholder={ekgError ? 'Enter system manually' : ''}
              />
              {ekgError && !system && (
                <div className="text-danger small mt-1">
                  Could not auto-populate system. Please enter manually.
                </div>
              )}
            </>
          )}
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading || !isFormSubmittable()}
          >
            Add new certificate
          </Button>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading || !isFormSubmittable()}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default CertificateForm;
